import { useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import { CHART_MOTION_MS } from '@vello/tokens';
import { countUpValue, easeOutCubic } from '@vello/charts-core';
import { cx, defaultFormatValue, useMotionState } from './kit';

export interface CountUpProps {
  /** The number to show. */
  readonly value: number;
  /** Format the shown number (money, count, percent …). It is called with the in-between numbers while counting. */
  readonly format?: (value: number) => string;
  /** Decimals kept while counting (default 0 — the in-between numbers are whole). Use the decimals of `value` for a fractional one. */
  readonly decimals?: number;
  /** Count duration in ms (default: the FX-05 count-up token, 400). */
  readonly durationMs?: number;
  /** `false` shows the value at once; default: counts in a real browser, except in a synthetic DOM. */
  readonly animate?: boolean;
  readonly className?: string;
}

/**
 * A number that counts up to its value (FX-05): from 0 on the first mount, from the previous value afterwards, in 400ms with an
 * ease-out cubic (`1 - (1 - p)^3`) and tabular numerals. Like the design's count-up it is NOT stilled by `prefers-reduced-motion`
 * (only the `.fx-*` effect classes are). In a test or any synthetic DOM — or with `animate={false}` — it renders the final value
 * on the very first render and never counts, so nothing needs a timer. Renders a `.tnum` (IBM Plex Mono, tabular) span around the
 * number only.
 */
export function CountUp({ value, format = defaultFormatValue, decimals = 0, durationMs = CHART_MOTION_MS.countUp, animate, className }: CountUpProps): ReactElement {
  const motion = useMotionState(animate);
  const [shown, setShown] = useState(value);
  const shownRef = useRef(value);
  const mounted = useRef(false);

  useLayoutEffect(() => {
    const first = !mounted.current;
    mounted.current = true;
    if (motion !== 'on' || typeof requestAnimationFrame !== 'function') {
      shownRef.current = value;
      setShown(value);
      return undefined;
    }
    const from = first ? 0 : shownRef.current;
    if (from === value) {
      setShown(value);
      return undefined;
    }
    const startedAt = performance.now();
    let frame = 0;
    const step = (now: number): void => {
      const t = Math.min(1, (now - startedAt) / durationMs);
      const next = countUpValue(from, value, easeOutCubic(t), decimals);
      shownRef.current = next;
      setShown(next);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    shownRef.current = from;
    setShown(from);
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, motion, durationMs, decimals]);

  return (
    <span className={cx('tnum', 'vello-countup', className)} data-motion={motion}>
      {format(motion === 'on' ? shown : value)}
    </span>
  );
}
